import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Plus,
  Grid3X3,
  List,
  MoreHorizontal,
  Mail,
  Phone,
  Building2,
  Users as UsersIcon,
  Loader2,
  Key,
  Copy,
  RefreshCw,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { usersAPI, teamsAPI } from '../../services/api';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from '../../hooks/use-toast';


// Generate random password
const generateRandomPassword = (length = 32) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-=';
  let password = '';
  for (let i = 0; i < length; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
};

const getRoleBadgeColor = (role) => {
  const colors = {
    ADMIN: 'bg-purple-100 text-purple-700',
    SUB_ADMIN: 'bg-blue-100 text-blue-700',
    SALES_MANAGER: 'bg-green-100 text-green-700',
    EMPLOYEE: 'bg-slate-100 text-slate-700',
    FINANCE_VIEWER: 'bg-amber-100 text-amber-700'
  };
  return colors[role] || 'bg-gray-100 text-gray-700';
};

const getStatusColor = (status) => {
  return status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700';
};

const TeamPage = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewType, setViewType] = useState('grid');
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [newMember, setNewMember] = useState({
    name: '',
    email: '',
    password: '',
    role: 'EMPLOYEE',
    team_id: '',
    manager_id: '',
    status: 'ACTIVE',
    force_password_change: true
  });

  // Check if current user is admin
  const isAdmin = currentUser?.role === 'ADMIN';

  // Fetch data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Use admin endpoint if user is admin
        const usersPromise = isAdmin ? usersAPI.listAdmin() : usersAPI.list();
        const [usersRes, teamsRes] = await Promise.all([
          usersPromise,
          teamsAPI.list()
        ]);
        setUsers(usersRes.data || []);
        setTeams(teamsRes.data || []);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isAdmin]);

  const handleGeneratePassword = () => {
    const password = generateRandomPassword(32);
    setNewMember({ ...newMember, password });
  };

  const copyPasswordToClipboard = () => {
    navigator.clipboard.writeText(newMember.password);
    setCopiedPassword(true);
    setTimeout(() => setCopiedPassword(false), 2000);
  };

  const getTeamName = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    return team?.team_name || 'No Team';
  };

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.role?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleAddMember = async () => {
    if (!newMember.password) {
      toast({
        variant: 'destructive',
        title: 'Missing password',
        description: 'Please set a password or generate a random one.'
      });
      return;
    }
    try {
      const response = await usersAPI.create({
        name: newMember.name,
        email: newMember.email,
        role: newMember.role,
        team_id: newMember.team_id || null,
        manager_id: newMember.manager_id || null,
        status: newMember.status,
        password: newMember.password,
        force_password_change: newMember.force_password_change
      });
      setUsers(prev => [...prev, response.data]);
      setIsAddMemberOpen(false);
      setNewMember({
        name: '',
        email: '',
        password: '',
        role: 'EMPLOYEE',
        team_id: '',
        manager_id: '',
        status: 'ACTIVE',
        force_password_change: true
      });
      setShowPassword(false);
    } catch (error) {
      console.error('Failed to add member:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to add member',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        <span className="ml-2 text-slate-500">Loading team...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Team</h1>
          <p className="text-slate-500">View and manage team members</p>
        </div>
        <Button 
          className="bg-slate-800 hover:bg-slate-900 text-white"
          onClick={() => setIsAddMemberOpen(true)}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Member
        </Button>
      </div>

      {/* Search and View Toggle */}
      <div className="flex items-center justify-between">
        <div className="relative w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search team members..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-white border-slate-200"
          />
        </div>
        <div className="flex items-center bg-slate-100 rounded-lg p-1">
          <Button
            variant={viewType === 'grid' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewType('grid')}
            className={cn(viewType === 'grid' ? 'bg-white shadow-sm' : '', 'h-8 w-8 p-0')}
          >
            <Grid3X3 className="w-4 h-4" />
          </Button>
          <Button
            variant={viewType === 'list' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewType('list')}
            className={cn(viewType === 'list' ? 'bg-white shadow-sm' : '', 'h-8 w-8 p-0')}
          >
            <List className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Grid View */}
      {viewType === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredUsers.map((user) => (
            <Card 
              key={user.id} 
              className="bg-white border-slate-200 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/team/${user.id}/profile`)}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-slate-700 flex items-center justify-center text-white font-semibold text-lg">
                      {user.avatar || user.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800">{user.name}</h3>
                      <p className="text-sm text-slate-500">
                        {user.role.replace('_', ' ')}
                      </p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/team/${user.id}/profile`); }}>View Profile</DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/team/${user.id}/activity`); }}>Activity Log</DropdownMenuItem>
                      <DropdownMenuItem>Edit Member</DropdownMenuItem>
                      <DropdownMenuItem>View Performance</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    {getTeamName(user.team_id)}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Mail className="w-4 h-4 text-slate-400" />
                    {user.email}
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-slate-100">
                  <div className="flex items-center justify-between">
                    <Badge className={getRoleBadgeColor(user.role)}>
                      {user.role.replace('_', ' ')}
                    </Badge>
                    <Badge className={getStatusColor(user.status)}>
                      {user.status}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* List View */}
      {viewType === 'list' && (
        <Card className="bg-white border-slate-200">
          <CardContent className="p-0">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left p-4 font-semibold text-slate-700">Member</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Role</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Team</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Email</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Status</th>
                  {isAdmin && <th className="text-left p-4 font-semibold text-slate-700">Password</th>}
                  <th className="w-[50px]"></th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr 
                    key={user.id} 
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/team/${user.id}/profile`)}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-white font-medium">
                          {user.avatar || user.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <span className="font-medium text-slate-800">{user.name}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge className={getRoleBadgeColor(user.role)}>
                        {user.role.replace('_', ' ')}
                      </Badge>
                    </td>
                    <td className="p-4 text-slate-600">
                      {getTeamName(user.team_id)}
                    </td>
                    <td className="p-4 text-slate-600">
                      {user.email}
                    </td>
                    <td className="p-4">
                      <Badge className={getStatusColor(user.status)}>
                        {user.status}
                      </Badge>
                    </td>
                    {isAdmin && (
                      <td className="p-4">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-2">
                                {user.has_changed_password ? (
                                  <Badge className="bg-emerald-100 text-emerald-700">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Changed
                                  </Badge>
                                ) : user.initial_password ? (
                                  <Badge className="bg-amber-100 text-amber-700">
                                    <Key className="w-3 h-3 mr-1" />
                                    Initial
                                  </Badge>
                                ) : (
                                  <Badge className="bg-slate-100 text-slate-700">
                                    <AlertCircle className="w-3 h-3 mr-1" />
                                    Unknown
                                  </Badge>
                                )}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              {user.has_changed_password 
                                ? `Password changed on ${user.password_changed_at ? new Date(user.password_changed_at).toLocaleDateString() : 'N/A'}`
                                : user.initial_password 
                                  ? `Initial password: ${user.initial_password}` 
                                  : 'Password status unknown'
                              }
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </td>
                    )}
                    <td className="p-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/team/${user.id}/profile`); }}>View Profile</DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/team/${user.id}/activity`); }}>Activity Log</DropdownMenuItem>
                          <DropdownMenuItem>Edit Member</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {filteredUsers.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-slate-400">
          <UsersIcon className="w-12 h-12 mb-3" />
          <p className="text-lg font-medium">No team members found</p>
          <p className="text-sm">Try adjusting your search</p>
        </div>
      )}

      {/* Add Member Modal */}
      <Dialog open={isAddMemberOpen} onOpenChange={setIsAddMemberOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
            <DialogDescription>
              Add a new member to the team. An invite email will be sent.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                value={newMember.name}
                onChange={(e) => setNewMember({...newMember, name: e.target.value})}
                placeholder="John Doe"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={newMember.email}
                onChange={(e) => setNewMember({...newMember, email: e.target.value})}
                placeholder="john@company.com"
              />
            </div>
            
            {/* Password Section */}
            <div className="grid gap-2">
              <Label htmlFor="password">Password *</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={newMember.password}
                    onChange={(e) => setNewMember({...newMember, password: e.target.value})}
                    placeholder="Enter password or generate"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleGeneratePassword}
                  className="flex gap-1"
                >
                  <RefreshCw className="w-4 h-4" />
                  Generate
                </Button>
                {newMember.password && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={copyPasswordToClipboard}
                    className="flex gap-1"
                  >
                    {copiedPassword ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                )}
              </div>
              {newMember.password && (
                <p className="text-xs text-slate-500">
                  Password length: {newMember.password.length} characters
                </p>
              )}
            </div>
            
            {/* Force Password Change */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="force_password_change"
                checked={newMember.force_password_change}
                onCheckedChange={(v) => setNewMember({...newMember, force_password_change: v})}
              />
              <Label htmlFor="force_password_change" className="text-sm cursor-pointer">
                Require password change on first login
              </Label>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Role *</Label>
                <Select 
                  value={newMember.role} 
                  onValueChange={(v) => setNewMember({...newMember, role: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ADMIN">Admin</SelectItem>
                    <SelectItem value="SUB_ADMIN">Sub Admin</SelectItem>
                    <SelectItem value="SALES_MANAGER">Sales Manager</SelectItem>
                    <SelectItem value="EMPLOYEE">Employee</SelectItem>
                    <SelectItem value="FINANCE_VIEWER">Finance Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Team</Label>
                <Select 
                  value={newMember.team_id} 
                  onValueChange={(v) => setNewMember({...newMember, team_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select team" />
                  </SelectTrigger>
                  <SelectContent>
                    {teams.map(team => (
                      <SelectItem key={team.id} value={team.id}>{team.team_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Manager</Label>
              <Select 
                value={newMember.manager_id} 
                onValueChange={(v) => setNewMember({...newMember, manager_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select manager" />
                </SelectTrigger>
                <SelectContent>
                  {users.filter(u => ['ADMIN', 'SALES_MANAGER'].includes(u.role)).map(user => (
                    <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddMemberOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddMember}
              disabled={!newMember.name || !newMember.email}
              className="bg-slate-800 hover:bg-slate-900"
            >
              Add Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamPage;
